/**
 * #677 — the on-surface mirror of Settings → Dashboard and ladders → "Resting
 * orders", for how a ladder rung presents the trader's own resting orders.
 *
 * ─── Why it lives in the StatusBar ─────────────────────────────────────────
 *
 * The issue asks for this beside "the relevant ladder/view-density controls".
 * Those controls exist — on the GameDetail sections — but they govern
 * `CompactOutcomeBands`, which is the one own-order surface this preference
 * deliberately does NOT change (no per-order cancel there; see
 * RestingOrderGroup's header). A control sitting beside a surface it has no
 * effect on is worse than one sitting somewhere neutral.
 *
 * The three surfaces it DOES change — the dashboard moneyline ladder, the
 * spread/total ladder and the LT2 two-sided ladder — live on two views with no
 * density cluster to join. The StatusBar already hosts `OddsFeeToggles`, whose
 * own docs describe them as flipping global display settings "app-wide", and
 * it is on screen on every one of those views. So this is read as the clause's
 * intent (an app-wide display preference belongs with the app-wide display
 * toggles) rather than as a literal location.
 *
 * ─── Why it is always visible ──────────────────────────────────────────────
 *
 * It would be tidier to render this only when the trader holds resting orders.
 * But a control that comes and goes cannot be set in advance — a trader who
 * wants Expanded ready BEFORE they have orders would have to place one first —
 * and its arrival would shift its neighbours in a bar people read positionally.
 *
 * Writes the same `displaySettings.restingOrderDisplay` as the Settings row:
 * one canonical value, two doors. Do not give this component local state.
 */
import { cn } from '@/lib/infra/utils';
import { INLINE_CHIP_LIMIT, useDisplaySettings } from '@/stores/displaySettings';

export function RestingOrderDisplayToggle({ className }: { className?: string }) {
  const restingOrderDisplay = useDisplaySettings((s) => s.restingOrderDisplay);
  const setRestingOrderDisplay = useDisplaySettings((s) => s.setRestingOrderDisplay);
  const expanded = restingOrderDisplay === 'expanded';

  return (
    <button
      onClick={() => setRestingOrderDisplay(expanded ? 'compact' : 'expanded')}
      // Both titles name what is on screen now AND what a click does, matching
      // the Fees/Raw idiom beside it. The compact one states the ordering rule
      // because that is what makes a `+N` honest: what it hides is the BACK of
      // the queue, never a nearer-to-filling order.
      // Built from INLINE_CHIP_LIMIT rather than spelling the number: the
      // constant is documented as a one-line knob, and copy that hardcoded it
      // would go quietly wrong the moment someone turned it.
      title={
        expanded
          ? `Ladder rungs show every one of your resting orders, front of the queue first — click for the compact ${INLINE_CHIP_LIMIT}, rest behind +N`
          : `Ladder rungs show the ${INLINE_CHIP_LIMIT} of your resting orders nearest the front of the queue, the rest behind +N — click to show every one`
      }
      aria-pressed={expanded}
      className={cn(
        'rounded-md border px-2 py-1 font-mono text-[11px] transition-colors',
        expanded
          ? 'border-primary text-primary'
          : 'border-border text-muted-foreground hover:text-foreground',
        className,
      )}
    >
      {expanded ? 'Orders all' : `Orders ${INLINE_CHIP_LIMIT}`}
    </button>
  );
}
